/**
 * SOLID Review
 * S — Strong. One concern: the claim this client is asked to believe or lift — whether it can be believed, the dice
 *     it stands for, the floor it leaves.
 * O — Good. A new question about the claim is one more query.
 * L — N/A — no inheritance hierarchy (private constructor).
 * I — Strong. Two factories, three questions, the snapshot.
 * D — Strong. The server's dice ranking and the table's schema type.
 */
import type { DiceRanking } from '../dice/DiceRanking';
import type { IStateClientTable } from '../ports/schemas/ClientTable';

/** The claim as the table stores it: the score and the server's name for it. */
type TOpponentClaim = NonNullable<IStateClientTable['opponentClaim']>;

/**
 * THE CLAIM ON THE TABLE, AS A VALUE OBJECT (frontend-domain-oo P3, 2026-10-26; handbook §28.270). Meyer cannot be
 * believed, only lifted, and the rule was written twice: the believe plan's pre-check (`decidePlanBelieve`) and
 * the decider's button (`deriveTable`'s `canBelieve`). It is written once here — Meyer as the server's ranking
 * marks it (frontend-domain-oo P2). Before the ranking has arrived nothing is Meyer, and the server, which refuses
 * a believed Meyer itself, has the last word.
 */
export class Claim {
  private constructor(private readonly claim: TOpponentClaim) {}

  /** The claim the table holds. */
  static of(claim: TOpponentClaim): Claim {
    return new Claim(claim);
  }

  /** A claim as the server announced it: the score, and its label as sent. */
  static announced(score: number, label: string): Claim {
    const claim: TOpponentClaim = { score, label };
    return new Claim(claim);
  }

  /** The score claimed — once believed, the floor the next roller must beat. */
  get score(): number {
    return this.claim.score;
  }

  /** Anything but Meyer may be believed. */
  canBeBelieved(ranking: DiceRanking): boolean {
    return !ranking.isMeyer(this.claim.score);
  }

  /** The claimed combination as two dice, high first — none for a score the ranking does not hold. */
  dice(ranking: DiceRanking): [number, number] | null {
    return ranking.diceOf(this.claim.score);
  }

  toSnapshot(): TOpponentClaim {
    return this.claim;
  }
}
